/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  ArrowLeft,
  Building,
  DollarSign,
  FileText,
  Target,
  Users,
  Eye,
  Edit,
  Trash2,
  TrendingUp,
  Phone,
  UserPlus,
  ArrowRightLeft,
} from "lucide-react";
import { useState } from "react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/app/_module/components/ui/tooltip";
import { Button } from "@/app/_module/components/ui/button";
import { SimplifiedMFAModal } from "@/app/_module/components/SimplifiedMFAModal";
import { RemoveAgentDialog, type RemoveAgentMode } from "./RemoveAgentDialog";
import { useDisableEligibility, useRequestDeactivation, useSuspendAgent } from "../../hooks/use-hook";
import { verifyMFAForAction, resendSMSForAction } from "@/app/_module/services/auth";
import { useToast } from "@/app/_module/hooks/use-toast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/_module/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/_module/components/ui/avatar";
import { getInitialsFromFullName } from "@/app/_module/utils/get-initials";
import type { SubAgency, AgencyUserWithAgency } from "../../types/types";
import { useAgencyStats, useDeleteAgencyUser, useUpdateAgencyUserRole } from "../../hooks/use-hook";
import EditUserRoleModal from "./EditUserRole";
import { MoveAgentModal } from "./MoveAgentModal";
import { BulkMoveAgentsModal } from "./BulkMoveAgentsModal";
import { CarrierBreakdown } from "./CarrierBreakdown";
import { SubAgenciesContainer } from "./SubAgenciesContainer";
import { isDemoMode } from "@/app/_module/utils/demoMode";

interface SubAgencyDetailsProps {
  subAgency: SubAgency;
  onBack: () => void;
  onViewAgentDetails?: (agencyUser: AgencyUserWithAgency) => void;
  onViewSubAgency?: (subAgency: SubAgency) => void;
  period?: '6-months' | '12-months' | 'year-to-date';
  startDate?: string;
  endDate?: string;
  parentName?: string; // Name of the parent to show in back button
}

export const SubAgencyDetails = function({
  subAgency,
  onBack,
  onViewAgentDetails,
  onViewSubAgency,
  period = '6-months',
  startDate,
  endDate,
  parentName,
}: SubAgencyDetailsProps) {
  const [userToDelete, setUserToDelete] = useState<AgencyUserWithAgency | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [showBulkMove, setShowBulkMove] = useState(false);
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [showRemoveChoice, setShowRemoveChoice] = useState(false);
  const [removeMode, setRemoveMode] = useState<RemoveAgentMode>("remove");
  const [scheduleDeletion, setScheduleDeletion] = useState(false);
  const { toast } = useToast();
  const deleteAgencyUserMutation = useDeleteAgencyUser();

  // Filter to users belonging to this sub-agency, but exclude parent /
  // grandparent owners who get auto-assigned. Backend hands us
  // `ancestorOwnerIds` — the set of user_ids that own ANY ancestor agency.
  // Without this filter, every higher-level owner shows up in every
  // sub-agency below them, which the user explicitly didn't want.
  //
  // Example for "Trigga P Agency":
  //   - palogjurashaj    → only owner of Trigga P                → KEEP
  //   - moeray           → also owner of parent Duse Agency      → SKIP
  //   - brandondusevic1  → also owner of parent Duse Agency      → SKIP
  //   - nikkgjurashaj    → regular agent (not in ancestor list)  → KEEP
  const ancestorOwnerSet = new Set<string>(
    Array.isArray((subAgency as any).ancestorOwnerIds)
      ? (subAgency as any).ancestorOwnerIds
      : [],
  );
  const subAgencyAgents = subAgency.agencyUsers?.filter(
    (agencyUser: any) => {
      if (agencyUser.agencyId !== subAgency.id) return false;
      // Exclude auto-assigned parent/grandparent owners
      if (agencyUser.userId && ancestorOwnerSet.has(agencyUser.userId)) return false;
      return true;
    },
  ) ?? [];

  // Calculate agent count from filtered data
  const agentCount = subAgencyAgents.length;

  // Fetch sub-agency statistics from backend using agency stats endpoint
  // This returns stats FOR this specific sub-agency (not its children)
  const { data: statsData } = useAgencyStats(subAgency.id, period, startDate, endDate);

  // Fetch parent agency stats for contribution calculation
  const { data: parentStatsData } = useAgencyStats(subAgency.parentAgencyId || '', period, startDate, endDate);

  // Extract stats from the API response structure (same as AgencyWideSummary)
  const stats = statsData as any;
  const parentStats = parentStatsData as any;
  const totalPremium = stats?.totalPremium || 0;
  // Issued-only (Sold & Approved). totalPremium is gross and includes Underwriting.
  const issuedPremium = (stats as { issuedPremium?: number } | undefined)?.issuedPremium || 0;
  const policiesSold = stats?.totalPolicies || 0;
  const closingRatio = stats?.closingRatio || 0;
  const averagePolicySize = stats?.averagePolicySize || 0;
  const averageCallTime = stats?.averageCallTime || 0;
  const newClients = stats?.newClients || 0;

  // Calculate contribution percentages
  const parentTotalPremium = parentStats?.totalPremium || 0;
  const parentTotalPolicies = parentStats?.totalPolicies || 0;
  const premiumContribution = parentTotalPremium > 0
    ? ((totalPremium / parentTotalPremium) * 100).toFixed(1)
    : '0.0';
  const policyContribution = parentTotalPolicies > 0
    ? ((policiesSold / parentTotalPolicies) * 100).toFixed(1)
    : '0.0';

  const subAgencyStats = [
    {
      title: "Total Production",
      value: `$${Math.round(totalPremium).toLocaleString()}`,
      icon: DollarSign,
      description: `${premiumContribution}% of parent agency total`,
      tooltip:
        "This is your agency total reported production. Everything marked sold and approved as well as in underwriting.",
    },
    {
      title: "Total Issued Sold & Approved Production",
      value: `$${Math.round(issuedPremium).toLocaleString()}`,
      icon: DollarSign,
      description: totalPremium > 0
        ? `${((issuedPremium / totalPremium) * 100).toFixed(1)}% of reported production`
        : "Issued business only",
      tooltip:
        "This is your agency total reported net production. Everything marked with sold and approved strictly shows up here.",
    },
    {
      title: "Total Policies",
      value: policiesSold.toString(),
      icon: FileText,
      description: `${policyContribution}% of parent agency total`,
      tooltip:
        "Every policy marked sold and approved as well as in underwriting.",
    },
    {
      title: "Average Policy Size",
      value: `$${Math.round(averagePolicySize).toLocaleString()}`,
      icon: TrendingUp,
      description: "Per policy value",
    },
    {
      title: "Closing Ratio",
      value: `${(closingRatio || 0).toFixed(1)}%`,
      icon: Target,
      description: "Sub-agency average",
    },
    {
      title: "Average Call Duration",
      value: `${(averageCallTime || 0).toFixed(1)} min`,
      icon: Phone,
      description: "Per call duration",
    },
    {
      title: "Total Leads",
      value: newClients.toLocaleString(),
      icon: UserPlus,
      description: "Agency-wide this period",
    },
    {
      title: "Agent Count",
      value: agentCount.toString(),
      icon: Users,
      description: "Active agents",
      tooltip: "Includes the sub-agency owner as well as their agents and admins.",
    },
  ];

  const handleDeleteUser = (user: AgencyUserWithAgency) => {
    // DEMO SANDBOX: removing an agent deletes agency membership for good — the seeded
    // downline is what makes leaderboards, production reports and the agency tree look
    // real, so a visitor pruning it would hand the NEXT visitor a hollowed-out agency.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Removing agents is disabled in the demo sandbox. Start a free trial to manage your real team.",
        }),
      );
      return;
    }

    setUserToDelete(user);
    setShowRemoveChoice(true);
  };

  const requestDeactivationMutation = useRequestDeactivation();
  const suspendMutation = useSuspendAgent();
  const { data: eligibility } = useDisableEligibility(
    showRemoveChoice ? (subAgency.id) : undefined,
    showRemoveChoice ? userToDelete?.userId : undefined,
  );

  const handleRemoveChoice = async (
    mode: RemoveAgentMode,
    opts?: { scheduleDeletion: boolean; autoDisableAfterDays: number | null },
  ) => {
    setShowRemoveChoice(false); // not via onOpenChange: that path clears userToDelete
    if (mode === "suspend") {
      if (!userToDelete) return;
      const who = userToDelete;
      try {
        await suspendMutation.mutateAsync({
          agencyId: subAgency.id,
          userId: who.userId,
          autoDisableAfterDays: opts?.autoDisableAfterDays ?? null,
        });
        toast({ title: "Agent suspended", description: `${who.user.name} can no longer sign in. Everything else is untouched.` });
      } catch (error: any) {
        toast({ title: "Couldn't suspend", description: error?.message || "Please try again.", variant: "destructive" });
      }
      setUserToDelete(null);
      return;
    }
    if (mode === "disable") setScheduleDeletion(!!opts?.scheduleDeletion);
    if (mode === "request") {
      if (!userToDelete) return;
      try {
        const res: any = await requestDeactivationMutation.mutateAsync({
          agencyId: subAgency.id,
          userId: userToDelete.userId,
        });
        toast({ title: "Request sent", description: res?.message || "InsuraCentral will review the deactivation request." });
      } catch (error: any) {
        toast({ title: "Couldn't send request", description: error?.message || "Please try again.", variant: "destructive" });
      }
      setUserToDelete(null);
      return;
    }
    setRemoveMode(mode);
    setShowMFAModal(true);
  };

  const handleMFAVerify = async (code: string, method: 'totp' | 'sms'): Promise<boolean> => {
    try {
      const verified = await verifyMFAForAction(code, method);
      return verified;
    } catch (error) {
      return false;
    }
  };

  const handleMFAVerified = async () => {
    if (userToDelete) {
      try {
        await deleteAgencyUserMutation.mutateAsync({
          agencyId: subAgency.id,
          data: {
            userId: userToDelete.userId,
            role: userToDelete.role,
            disableAccount: removeMode === "disable",
            scheduleDeletion: removeMode === "disable" ? scheduleDeletion : false,
          },
        });
        setUserToDelete(null);
        toast({
          title: removeMode === "disable" ? "Account disabled" : "Agent removed",
          description:
            removeMode === "disable"
              ? `${userToDelete.user.name} has been signed out, their subscription and numbers cancelled, and they were removed from the sub-agency.`
              : `${userToDelete.user.name} has been removed from the sub-agency.`,
        });
      } catch (error: any) {
        toast({
          title: "Error",
          description: error?.message || "Failed to remove agent from sub-agency",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button
          className="flex items-center gap-2"
          onClick={onBack}
          variant="outline"
        >
          <ArrowLeft className="h-4 w-4" />
          {parentName ? `Back to ${parentName}` : 'Back to Overview'}
        </Button>
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Building className="h-6 w-6 text-primary" />
            {subAgency.name}
          </h2>
          <p className="text-muted-foreground">
            {subAgency.description || 'No description available'} • Established {new Date(subAgency.createdAt).getFullYear()}
          </p>
        </div>
      </div>

      {/* Sub-agency stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {subAgencyStats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                {stat.title}
                {(stat as { tooltip?: string }).tooltip && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" aria-label="Info" className="inline-flex items-center cursor-help">
                          <Info className="h-4 w-4 text-primary/80 hover:text-primary transition-colors" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-56 text-xs">{(stat as { tooltip?: string }).tooltip}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {stat.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Agency Info */}
      <Card>
        <CardHeader>
          <CardTitle>Agency Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <p className="mt-1">{subAgency.description || '—'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Contact Email</label>
              <p className="mt-1">{subAgency.contactEmail || '—'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <div className="mt-1">
                <Badge variant={subAgency.isActive ? "default" : "secondary"}>
                  {subAgency.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Created</label>
              <p className="mt-1">{new Date(subAgency.createdAt || subAgency.updatedAt).toLocaleDateString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Agents table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Sub-Agency Agents ({agentCount})</CardTitle>
          {selectedUserIds.size > 0 && (
            <Button size="sm" onClick={() => setShowBulkMove(true)}>
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Move {selectedUserIds.size} Agent{selectedUserIds.size === 1 ? "" : "s"}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <BulkMoveAgentsModal
            open={showBulkMove}
            onClose={() => setShowBulkMove(false)}
            sourceAgencyId={subAgency.id}
            sourceAgencyName={subAgency.name}
            selectedUserIds={Array.from(selectedUserIds)}
            onSuccess={() => setSelectedUserIds(new Set())}
            targetTreeRootId={subAgency.parentAgencyId}
            targetTreeRootName={parentName}
          />
          {subAgencyAgents.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <input
                      type="checkbox"
                      aria-label="Select all agents"
                      checked={subAgencyAgents.length > 0 && subAgencyAgents.every((a: any) => selectedUserIds.has(a.userId))}
                      onChange={(e) =>
                        setSelectedUserIds((prev) => {
                          const next = new Set(prev);
                          subAgencyAgents.forEach((a: any) => (e.target.checked ? next.add(a.userId) : next.delete(a.userId)));
                          return next;
                        })
                      }
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Account Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>First Login Today</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subAgencyAgents.map((agencyUser: any) => (
                  <TableRow key={agencyUser.id}>
                    <TableCell className="w-8" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${agencyUser.user?.name || "agent"}`}
                        checked={selectedUserIds.has(agencyUser.userId)}
                        onChange={(e) =>
                          setSelectedUserIds((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(agencyUser.userId);
                            else next.delete(agencyUser.userId);
                            return next;
                          })
                        }
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 border border-primary/20 shadow-sm">
                          <AvatarImage
                            src={agencyUser.user?.profileImageUrl || agencyUser.user?.profile_image_url || undefined}
                            alt={agencyUser.user?.name || ''}
                            className="object-cover"
                          />
                          <AvatarFallback className="text-sm bg-primary text-primary-foreground font-semibold">
                            {getInitialsFromFullName(
                              agencyUser.user?.name?.split(' ')[0] || 'U',
                              agencyUser.user?.name?.split(' ')[1] || 'N',
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <span>{agencyUser.user?.name || '—'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{agencyUser.user?.email || '—'}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          agencyUser.role?.toLowerCase() === 'owner'
                            ? 'bg-red-100 text-red-800 border-red-300 hover:bg-red-100'
                            : agencyUser.role?.toLowerCase() === 'manager' || agencyUser.role?.toLowerCase() === 'admin'
                            ? 'bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-100'
                            : 'bg-white text-black border-gray-300 hover:bg-gray-50'
                        }
                      >
                        {agencyUser.role?.toLowerCase() === 'admin' ? 'Agency Admin' : (agencyUser.role || '—')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{agencyUser.user?.title || '—'}</TableCell>
                    <TableCell>
                      {(() => {
                        const at = (agencyUser.user?.accountType || '').toLowerCase();
                        const label = at === 'full_fledged' ? 'Full-Fledged'
                          : at === 'basic' ? 'Basic'
                          : at === 'trial' ? 'Trial'
                          : at === 'super_agent' ? 'Super Agent'
                          : at === 'enterprise' ? 'Enterprise'
                          : agencyUser.user?.accountType || '—';
                        return (
                          <Badge variant="outline" className="bg-white text-black border-gray-300 hover:bg-gray-50">
                            {label}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // Status — match Agency Agents table semantics: derive from user_status
                        // first, fall back to is_online → available/offline, and honor on_call / idle.
                        const status = agencyUser.user?.userStatus || (agencyUser.user?.isOnline ? 'available' : 'offline');
                        const statusConfig: Record<string, { dot: string; badge: string; label: string; variant: 'default' | 'outline' }> = {
                          available: { dot: 'bg-green-500 animate-pulse', badge: 'bg-green-100 text-green-800 border-green-300 hover:bg-green-100', label: 'Online', variant: 'default' },
                          idle: { dot: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-100', label: 'Idle', variant: 'default' },
                          on_call: { dot: 'bg-blue-500 animate-pulse', badge: 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-100', label: 'On Call', variant: 'default' },
                          dnd: { dot: 'bg-red-500', badge: 'bg-red-100 text-red-800 border-red-300 hover:bg-red-100', label: 'Do Not Disturb', variant: 'default' },
                          offline: { dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-100', label: 'Offline', variant: 'outline' },
                        };
                        const config = statusConfig[status] || statusConfig.offline;
                        return (
                          <Badge variant={config.variant} className={`${config.badge} inline-flex items-center gap-1.5`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
                            {config.label}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const firstLogin = agencyUser.user?.firstLoginToday;
                        if (!firstLogin) return <span className="text-xs text-muted-foreground">—</span>;
                        const loginDate = new Date(firstLogin);
                        return (
                          <span className="text-xs font-medium text-foreground">
                            {loginDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const lastActive = agencyUser.user?.lastActiveAt;
                        if (!lastActive) return <span className="text-xs text-muted-foreground">—</span>;
                        const activeDate = new Date(lastActive);
                        const diffMin = Math.floor((Date.now() - activeDate.getTime()) / 60000);
                        if (diffMin < 1) return <span className="text-xs font-medium text-green-600">Just now</span>;
                        if (diffMin < 60) return <span className="text-xs text-muted-foreground">{diffMin}m ago</span>;
                        const diffHrs = Math.floor(diffMin / 60);
                        if (diffHrs < 24) return <span className="text-xs text-muted-foreground">{diffHrs}h ago</span>;
                        return <span className="text-xs text-muted-foreground">{activeDate.toLocaleDateString()}</span>;
                      })()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 items-center justify-center">
                        {onViewAgentDetails && (
                          <Button
                            className="h-8 w-8 p-0 cursor-pointer"
                            onClick={() => onViewAgentDetails(agencyUser)}
                            size="sm"
                            title="View Performance Details"
                            variant="ghost"
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                        )}
                        <EditUserRoleModal
                          agencyId={subAgency.id}
                          agencyUser={agencyUser}
                          trigger={
                            <Button
                              className="h-8 w-8 p-0 cursor-pointer"
                              size="sm"
                              variant="outline"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          }
                        />
                        <MoveAgentModal
                          agencyUser={agencyUser}
                          currentAgencyId={subAgency.id}
                          currentAgencyName={subAgency.name}
                          trigger={
                            <Button
                              className="h-8 w-8 p-0 cursor-pointer"
                              size="sm"
                              variant="outline"
                              title="Move to sub-agency"
                            >
                              <ArrowRightLeft className="h-3 w-3" />
                            </Button>
                          }
                        />
                        <Button
                          className="h-8 w-8 p-0 cursor-pointer"
                          onClick={() => handleDeleteUser(agencyUser)}
                          size="sm"
                          variant="destructive"
                        >
                          <Trash2 className="h-3 w-3 text-white" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No agents found for this sub-agency.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sub-Agencies */}
      {onViewSubAgency && (
        <Card>
          <CardContent className="pt-6">
            <SubAgenciesContainer
              parentAgencyId={subAgency.id}
              parentAgencyName={subAgency.name}
              onViewDetails={onViewSubAgency}
              onViewAgentDetails={onViewAgentDetails}
              period={period}
              startDate={startDate}
              endDate={endDate}
            />
          </CardContent>
        </Card>
      )}

      {/* Premium by Carrier - Pie Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Production by Carrier</CardTitle>
          <p className="text-sm text-muted-foreground">Breakdown of all premium sold by carriers in this sub-agency</p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            {/* Left side: Pie Chart with carrier names below */}
            <div className="flex-shrink-0">
              <CarrierBreakdown agencyId={subAgency.id} period={period} startDate={startDate} endDate={endDate} inTab showChartOnly />
            </div>
            {/* Right side: Data Table - fills remaining space */}
            <div className="flex-1 border rounded-lg overflow-hidden h-[500px]">
              <CarrierBreakdown agencyId={subAgency.id} period={period} startDate={startDate} endDate={endDate} inTab showTableOnly />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* MFA Verification Modal for Remove Agent */}
      <RemoveAgentDialog
        open={showRemoveChoice}
        onOpenChange={(open) => {
          setShowRemoveChoice(open);
          if (!open && !showMFAModal) setUserToDelete(null);
        }}
        agentName={userToDelete?.user.name || "this agent"}
        eligibility={eligibility}
        onChoose={handleRemoveChoice}
      />
      <SimplifiedMFAModal
        isOpen={showMFAModal}
        onClose={() => {
          setShowMFAModal(false);
          setUserToDelete(null);
        }}
        onVerified={handleMFAVerified}
        onVerify={handleMFAVerify}
        onSendSMS={resendSMSForAction}
        title={removeMode === "disable" ? "Verify to Disable Account" : "Verify to Remove Agent"}
        description={
          removeMode === "disable"
            ? `Please verify your identity with 2FA to disable ${userToDelete?.user.name || 'this agent'}'s account. They will be signed out, their subscription and numbers cancelled without refund, and removed from the sub-agency.`
            : `Please verify your identity with 2FA to remove ${userToDelete?.user.name || 'this agent'} from the sub-agency. They will become an independent agent.`
        }
      />
    </div>
  );
}
